import FlexColumn from "../Flexbox/FlexColumn";
import FlexRow from "../Flexbox/FlexRow";
import TextField from "../../display/TextField/TextField";
import { useMemo } from "react";

import sortAsc from "../../../../assets/images/common/svg/sort_asc.svg";
import sortDesc from "../../../../assets/images/common/svg/sort_desc.svg";
import sortNone from "../../../../assets/images/common/svg/sort_none.svg";

import "./Table_style.css";

export type TableSort = { key: string; order?: "asc" | "desc";};

type Props<T> = {
    columns: Array<{
        columnKey: string;
        columnHeader?: string;
        sortable?: boolean;
        width?: string | number;
    }>;
    rowData: Array<T>;
    cellParser: Array<{
        columnKey: string;
        callback: (val: T) => React.ReactNode;
    }>;
    idGetter: (val: T) => string;
    /** A row footer is a full table width component sitting underneath the main cells for a row */
    rowFooter?: (val: T) => React.ReactNode;
    isRowSelected?: (val: T) => boolean;
    /** A sort key and direction, used to enable column sorting */
    sort?: TableSort | null;
    /** An optional function for sorting rows. Used standalone, or in conjunction with sort */
    sorter?: (t1: T, t2: T) => number;
    onChangeSort?: (key: TableSort | null) => void;
    onRowClick?: (val: T) => void;
    onHeaderClick?: (key: string) => void;
    rowFilter?: ((val: T) => boolean) | null;
    showHeader?: boolean;
    className?: string;
};

export default function Table<T = Array<string>>({columns, rowData, cellParser, idGetter, rowFooter, isRowSelected, sort, sorter, onChangeSort, onRowClick, onHeaderClick, rowFilter, showHeader, className}: Props<T>): JSX.Element {
    const visibleRows = useMemo(
        () => {
            const rows = rowFilter == null
                ? rowData
                : rowData.filter(
                      (row) => rowFilter?.(row) ?? false
                  );

            return sorter == null ? rows : rows.sort(sorter);
        },
        [rowFilter, rowData, sorter]
    );

    return (
        <FlexColumn
            className={`generic-table-container${
                className ? ` ${className}` : ``
            }`}
        >
            {(showHeader === undefined || showHeader === true) && (
                <FlexRow className="generic-table-header-container">
                    {columns.map((col) => {
                        return (
                            <FlexRow
                                key={col.columnKey}
                                gap={8}
                                className={`generic-table-header-cell${
                                    col.sortable === true ? ` sortable` : ``
                                }`}
                                alignment="end"
                                style={
                                    col.width === undefined
                                        ? { width: "100%" }
                                        : {
                                              width:
                                                  typeof col.width === "string"
                                                      ? col.width
                                                      : `${col.width}px`,
                                              flexShrink:
                                                  typeof col.width === "string"
                                                      ? col.width.includes("px")
                                                          ? 0
                                                          : 1
                                                      : 0,
                                          }
                                }
                                onClick={() => {
                                    if (col.sortable === true) {
                                        if (
                                            sort?.key === col.columnKey
                                        ) {
                                            if (
                                                sort?.order ===
                                                    undefined ||
                                                sort?.order === "desc"
                                            ) {
                                                onChangeSort?.({
                                                    key: col.columnKey,
                                                    order: "asc",
                                                });
                                            } else {
                                                onChangeSort?.(null);
                                            }
                                        } else {
                                            onChangeSort?.({
                                                key: col.columnKey,
                                                order: "desc",
                                            });
                                        }
                                    }
                                }}
                            >
                                <TextField className="label">
                                    {col.columnHeader ?? ""}
                                </TextField>
                                {col.sortable && (
                                    <div className="sort-button">
                                        <img
                                            src={
                                                sort?.key ===
                                                col.columnKey
                                                    ? sort?.order ===
                                                          undefined ||
                                                      sort?.order ===
                                                          "desc"
                                                        ? sortDesc
                                                        : sortAsc
                                                    : sortNone
                                            }
                                            alt="V"
                                        />
                                    </div>
                                )}
                            </FlexRow>
                        );
                    })}
                </FlexRow>
            )}
            <FlexColumn className="generic-table-rows-container">
                {visibleRows.length > 0 ? (
                    visibleRows.map((rowEntry) => {
                        const rowID = idGetter(rowEntry);
                        const isSelected =
                            isRowSelected?.(rowEntry) ?? false;
                        const footerContents =
                            rowFooter === undefined
                                ? null
                                : rowFooter(rowEntry);
                        return (
                            <FlexColumn
                                key={rowID}
                                className={`generic-table-row-wrapper${
                                    isSelected ? " selected" : ""
                                }`}
                                onClick={() => {
                                    onRowClick?.(rowEntry);
                                }}
                            >
                                <FlexRow className={"generic-table-row"}>
                                    {columns.map((col) => {
                                        const parser = cellParser.find(
                                            (c) => c.columnKey === col.columnKey
                                        )?.callback;

                                        return (
                                            <div
                                                key={`${col.columnKey}-${rowID}`}
                                                className="generic-table-cell"
                                                style={
                                                    col.width === undefined
                                                        ? { width: "100%" }
                                                        : {
                                                              width:
                                                                  typeof col.width ===
                                                                  "string"
                                                                      ? col.width
                                                                      : `${col.width}px`,
                                                              flexShrink:
                                                                  typeof col.width ===
                                                                  "string"
                                                                      ? col.width.includes(
                                                                            "px"
                                                                        )
                                                                          ? 0
                                                                          : 1
                                                                      : 0,
                                                          }
                                                }
                                            >
                                                {parser ? (
                                                    parser(rowEntry)
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                        );
                                    })}
                                </FlexRow>
                                {footerContents !== null && <br />}
                                {footerContents !== null && (
                                    <FlexRow className="generic-table-row-footer-container">
                                        {footerContents}
                                    </FlexRow>
                                )}
                            </FlexColumn>
                        );
                    })
                ) : (
                    <div className="empty_table_row" />
                )}
            </FlexColumn>
        </FlexColumn>
    );
}
